import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { Cookies, withCookies } from 'react-cookie';
import PropTypes from 'prop-types';
import Routes from './Routes';
import * as auth from './libs/auth';
/** @type HhConfig */
import config from './config';
import AppDataLoaderReal from './containers/App/DataLoader/AppDataLoaderReal';
import InternalError from './InternalError';
import { COOKIE_MERCHANT_CODE, fixedComponents } from './const';

class App extends Component {
    static propTypes = {
        allCookies: PropTypes.shape({
            [COOKIE_MERCHANT_CODE]: PropTypes.string,
        }).isRequired,
        cookies: PropTypes.instanceOf(Cookies).isRequired,
        history: PropTypes.object.isRequired,
        location: PropTypes.shape({
            pathname: PropTypes.string,
            hash: PropTypes.string,
            search: PropTypes.string,
            state: PropTypes.object,
        }).isRequired,
    };

    static MERCHANT_CODE_DEFAULT = 'none';

    static MERCHANT_CODE_ADMIN = 'admin';

    /**
     * @param {Object} props
     */
    constructor(props) {
        super(props);

        this.state = {
            errorMessageTechnical: null,
            errorMessageUser: null,
            isAuthenticated: false,
            isAuthenticating: true,
            isDemoMode: true,
            isError: false,
            merchantCode: App.MERCHANT_CODE_DEFAULT,
            merchantSettings: this.merchantSettingsDefault,
            user: null,
        };

        this.logoutUser = this.logoutUser.bind(this);
        this.onAuthSuccess = this.onAuthSuccess.bind(this);
        this.refreshMerchantSettings = this.refreshMerchantSettings.bind(this);
        this.reload = this.reload.bind(this);
    }

    /**
     * @returns {Promise<void>}
     */
    async componentDidMount() {
        const { isAuthenticating } = this.state;
        if (!isAuthenticating) {
            this.setState({ isAuthenticating: true });
        }

        try {
            const merchantUser = await auth.tryToAuthenticateWithStoredTokens();
            if (merchantUser !== null) {
                await this.setAuthState(true, merchantUser);
            }
        } catch (error) {
            console.error(`App: Auth error: ${error.message}`);
        } finally {
            this.setState({ isAuthenticating: false });
        }
    }

    /**
     * @param {MerchantUser} merchantUser
     * @returns {Promise<void>}
     * @public
     */
    onAuthSuccess(merchantUser) {
        return this.setAuthState(true, merchantUser);
    }

    /**
     * @returns {AppData}
     */
    get merchantSettingsDefault() { // eslint-disable-line class-methods-use-this
        return {
            FEATURE_ENABLED_ABANDONED_CHECKOUT: false,
            FEATURE_ENABLED_PRODUCT_REVIEW: false,
            FEATURE_ENABLED_SHIPPING_NOTIFICATION: false,
            isPriceMatchEnabled: true,
            integrationType: 'empty',
            merchantRegistrationDate: new Date(new Date().getFullYear(), 0, 1),
            timezone: config.timezone,
            merchantType: 'default',
        };
    }

    /**
     * @param {boolean} isAuthenticated
     * @param {MerchantUser} merchantUser
     * @returns {Promise<void>}
     * @private
     */
    async setAuthState(isAuthenticated, merchantUser = null) {
        if (isAuthenticated && !merchantUser) {
            throw new Error('App: invalid auth state combination');
        }

        const merchantCode = await this.getMerchantCode(merchantUser); // 240ms
        const merchantSettings = await this.getMerchantSettings(isAuthenticated, merchantCode);
        this.setState({
            isAuthenticated,
            user: merchantUser,
            merchantCode,
            merchantSettings,
            // isDemoMode can be anything in non-authenticated state
            isDemoMode: merchantUser ? !merchantUser.isConfirmed || merchantCode === App.MERCHANT_CODE_ADMIN : true,
        });
    }

    /**
     * @param {MerchantUser} user
     * @returns {Promise<string>}
     * @private
     */
    async getMerchantCode(user = null) {
        let merchantCode;

        const domain = window.location.hostname.slice(0, window.location.hostname.indexOf('.'));
        if (config.domainToSource[domain] && config.knownSources.includes(config.domainToSource[domain])) {
            merchantCode = config.domainToSource[domain];
        }

        const { cookies } = this.props;

        if (!merchantCode) {
            merchantCode = cookies.get(COOKIE_MERCHANT_CODE);
        }

        if (!merchantCode && user) {
            merchantCode = await user.getMerchantCode();
        }

        if (!merchantCode) {
            merchantCode = App.MERCHANT_CODE_DEFAULT;
        }

        return merchantCode;
    }

    /**
     * @param {boolean} isAuthenticated
     * @param {string} merchantCode
     * @returns {Promise<AppData>}
     * @private
     */
    async getMerchantSettings(isAuthenticated, merchantCode = App.MERCHANT_CODE_DEFAULT) {
        try {
            const useDefaultSettings = !isAuthenticated
                || [App.MERCHANT_CODE_DEFAULT, App.MERCHANT_CODE_ADMIN].includes(merchantCode);
            if (useDefaultSettings) {
                return this.merchantSettingsDefault;
            }

            const appDataLoader = new AppDataLoaderReal(config);
            const data = await appDataLoader.loadData(merchantCode);

            return data;
        } catch (error) {
            console.error(`Failed to load merchant settings: ${error.message}`);
            this.setState({
                errorMessageTechnical: `${error.message}`,
                errorMessageUser: 'Failed to load merchant settings',
                isError: true,
            });

            return this.merchantSettingsDefault;
        }
    }

    /**
     * @param {string} merchantCode
     * @returns {Promise<void>}
     * @public
     */
    async refreshMerchantSettings(merchantCode) {
        const merchantSettings = await this.getMerchantSettings(true, merchantCode);
        this.setState({ merchantSettings });
    }

    /**
     * @returns {Promise<void>}
     * @public
     */
    async reload() {
        const merchantCode = await this.getMerchantCode();
        const merchantSettings = await this.getMerchantSettings(true, merchantCode);

        this.setState({
            merchantCode,
            merchantSettings,
            isDemoMode: merchantCode === App.MERCHANT_CODE_ADMIN,
        });
    }

    /**
     * @returns {Promise<void>}
     * @public
     */
    async logoutUser() {
        const { cookies, history, location: { search } } = this.props;
        cookies.remove(COOKIE_MERCHANT_CODE, { path: '/' });
        auth.signOutUser();
        await this.setAuthState(false);
        history.push({ pathname: '/', search });
    }

    /**
     * @param {Object} nextProps
     * @param {Object} _nextState
     * @param {Object} _nextContext
     * @returns void
     */
    async componentWillUpdate(nextProps, _nextState, _nextContext) { // eslint-disable-line
        const { allCookies, location } = this.props;
        const { isDemoMode } = this.state;
        const { cookies: nextCookies } = nextProps;

        const isMerchantCodeChanged = allCookies[COOKIE_MERCHANT_CODE] !== nextCookies.get(COOKIE_MERCHANT_CODE);
        const isHardReloadRequired = () => {
            return isDemoMode || !fixedComponents.includes(location.pathname);
        };
        if (isMerchantCodeChanged && !isHardReloadRequired()) {
            await this.reload();
        }
    }

    /**
     * @returns {React.ReactNode}
     */
    render() {
        const {
            isAuthenticating,
            isAuthenticated,
            isDemoMode,
            isError,
            merchantCode,
            merchantSettings,
            user,
        } = this.state;

        if (isAuthenticating) {
            return false;
        }

        if (isError) {
            const { errorMessageUser, errorMessageTechnical } = this.state;
            return (
                <InternalError
                    errorMessage={errorMessageUser}
                    errorMessageTechnical={errorMessageTechnical}
                    supportEmail={config.app.email.hello}
                />
            );
        }

        const { cookies } = this.props;

        const childProps = {
            config,
            cookies,
            isAuthenticated,
            isDemoMode,
            merchantCode,
            merchantSettings,
            user,
            logoutUser: this.logoutUser,
            onAuthSuccess: this.onAuthSuccess,
            reload: this.reload,
            refreshMerchantSettings: () => { return this.refreshMerchantSettings(merchantCode); },
        };

        return <Routes childProps={childProps} />;
    }
}

export default withRouter(withCookies(App));
